/**
 * Vendor FAQ — source of truth.
 *
 * Rendered in the backoffice Help page (`/vendor/help`) and also published to
 * the Crisp knowledgebase. When you edit answers here, mirror the change in
 * Crisp (https://app.crisp.chat/) so the two surfaces don't drift.
 *
 * Conventions:
 * - `id` is a URL-safe slug. Used as the hash on `/vendor/help#<id>` so we can
 *   deep-link from onboarding cards, support replies, and Crisp articles.
 * - `category` maps the FAQ to a sidebar group on the Help page.
 * - `answer` is plain prose. Use blank lines (`\n\n`) for paragraph breaks.
 *   Avoid HTML/Markdown so the same string drops cleanly into Crisp.
 * - `onboardingStep` (optional) lets the OnboardingPage deep-link a step's
 *   "Questions about this step?" hint to the right Help category.
 */

export type FaqCategoryId =
  | 'getting-started'
  | 'connect-booking-system'
  | 'products-inventory'
  | 'payments-payouts'
  | 'supplier-agreement'
  | 'ai-storefront'
  | 'pricing-plans'
  | 'account-security'

export interface FaqCategory {
  id: FaqCategoryId
  label: string
  description: string
}

export interface FaqItem {
  id: string
  category: FaqCategoryId
  question: string
  answer: string
}

export const faqCategories: FaqCategory[] = [
  {
    id: 'getting-started',
    label: 'Getting Started',
    description: 'New to Tixxly? Start here.',
  },
  {
    id: 'connect-booking-system',
    label: 'Connect Your Booking System',
    description: 'Linking Bokun, FareHarbor, and other providers.',
  },
  {
    id: 'products-inventory',
    label: 'Products & Inventory',
    description: 'Feeds, experiences, and how product data flows through Tixxly.',
  },
  {
    id: 'payments-payouts',
    label: 'Payments & Payouts',
    description: 'Stripe Connect, fees, refunds, and getting paid.',
  },
  {
    id: 'supplier-agreement',
    label: 'Supplier Agreement',
    description: 'Terms, refunds, and merchant of record.',
  },
  {
    id: 'ai-storefront',
    label: 'AI Storefront & Discovery',
    description: 'Your storefront and how AI agents find your products.',
  },
  {
    id: 'pricing-plans',
    label: 'Pricing & Plans',
    description: 'Free, Growth, Pro, and Infrastructure.',
  },
  {
    id: 'account-security',
    label: 'Account & Security',
    description: 'Logins, team members, and your data.',
  },
]

/** Maps an onboarding step id (from OnboardingPage) to a FAQ category. */
export const onboardingStepToCategory: Record<string, FaqCategoryId> = {
  connect_provider: 'connect-booking-system',
  sync_feeds: 'products-inventory',
  sync_experiences: 'products-inventory',
  connect_payment: 'payments-payouts',
  accept_agreement: 'supplier-agreement',
  setup_ai_storefront: 'ai-storefront',
}

export const faqItems: FaqItem[] = [
  // ─── Getting Started ──────────────────────────────────────────────────────
  {
    id: 'what-is-tixxly',
    category: 'getting-started',
    question: 'What is Tixxly?',
    answer:
      "Tixxly is AI travel commerce infrastructure. You connect your booking system once, we structure your inventory for AI ecosystems like ChatGPT, Gemini, and Claude, and AI agents discover and book your experiences directly.\n\nWe're not an OTA. There are no commissions — you keep 100% of your booking revenue and pay a flat monthly subscription instead.",
  },
  {
    id: 'how-tixxly-works',
    category: 'getting-started',
    question: 'How does Tixxly work end-to-end?',
    answer:
      "1. Connect your booking system (Bokun today; FareHarbor and others on the roadmap).\n2. Sync your feeds and activate the experiences you want listed.\n3. Connect Stripe so booking payments route directly to your bank.\n4. Accept the supplier agreement.\n5. Set up your AI storefront.\n\nOnce live, your products are discoverable inside AI agents and bookable through your storefront at stores.tixxly.ai/v/<your-username>.",
  },
  {
    id: 'how-long-onboarding-takes',
    category: 'getting-started',
    question: 'How long does onboarding take?',
    answer:
      'Most vendors complete the six steps in under 30 minutes once they have their booking system credentials and Stripe account ready. Initial feed sync from Bokun usually completes in 2–10 minutes depending on inventory size.',
  },
  {
    id: 'what-do-i-need-to-start',
    category: 'getting-started',
    question: 'What do I need before I start?',
    answer:
      'A Bokun account (other booking systems on the roadmap), a Stripe-eligible bank account in a supported country, and your business details — business name, website URL, contact email, and a cancellation policy URL. You will also review and accept the supplier agreement during onboarding.',
  },
  {
    id: 'can-i-skip-steps',
    category: 'getting-started',
    question: 'Can I skip onboarding steps?',
    answer:
      "No. Every step is required. Each step unlocks the next — for example, you can't activate experiences before your feeds sync, and the AI Storefront step is locked until the previous five are complete. This keeps every live vendor in a state where AI agents can actually fulfil bookings.",
  },

  // ─── Connect Your Booking System ──────────────────────────────────────────
  {
    id: 'supported-booking-systems',
    category: 'connect-booking-system',
    question: 'Which booking systems do you support?',
    answer:
      "Today: Bokun.\n\nOn the roadmap: FareHarbor, Viator (affiliate / embedded checkout), GetYourGuide, and direct hotel PMS bridges (Cloudbeds, Stayntouch, Opera Cloud).\n\nIf your system isn't listed, contact us — we prioritize integrations based on supplier demand.",
  },
  {
    id: 'find-bokun-credentials',
    category: 'connect-booking-system',
    question: 'How do I find my Bokun API credentials?',
    answer:
      "In Bokun: Settings → API → Generate a new API key. Copy the access key and secret key, then paste them into Tixxly's Integrations → Connect Bokun page.\n\nTixxly uses read-only access. We pull product, availability, and booking data; we never modify your inventory in Bokun.",
  },
  {
    id: 'when-fareharbor',
    category: 'connect-booking-system',
    question: 'I use FareHarbor — when can I connect?',
    answer:
      "FareHarbor is on the roadmap and prioritized based on demand. If you'd like to be notified when it's live (and to influence prioritization), email hello@tixxly.ai with your FareHarbor account details.",
  },
  {
    id: 'do-i-need-to-leave-otas',
    category: 'connect-booking-system',
    question: 'Do I need to disconnect from Viator or GetYourGuide?',
    answer:
      "No. Tixxly is infrastructure, not an OTA. We don't compete for your direct customers, and AI booking traffic is incremental to OTA traffic. Keep your existing distribution channels and add Tixxly alongside them.",
  },
  {
    id: 'products-not-syncing',
    category: 'connect-booking-system',
    question: "My products aren't syncing — what's wrong?",
    answer:
      "Check Inventory → Feeds. If a feed shows 'failed', click into it for the underlying error.\n\nCommon causes:\n- API key revoked or rotated upstream\n- Products marked 'draft' or 'inactive' in your booking system\n- Rate limits during very large initial syncs\n\nIf the feed succeeds but specific products are missing, the products are usually unpublished upstream. Publish them in your booking system and click 'Re-sync now' on the feed.",
  },

  // ─── Products & Inventory ─────────────────────────────────────────────────
  {
    id: 'feeds-vs-experiences',
    category: 'products-inventory',
    question: "What's the difference between feeds and experiences?",
    answer:
      "A feed is the raw connection to your booking system — it pulls everything available.\n\nExperiences are the individual tours or activities you actively want listed on your AI storefront. After a feed syncs, you choose which experiences to activate. Inactive experiences stay private and aren't sent to AI platforms.",
  },
  {
    id: 'feed-sync-time',
    category: 'products-inventory',
    question: 'How long does a feed sync take?',
    answer:
      'Initial sync: 2–10 minutes for catalogs under ~200 products. Larger catalogs can take up to 30 minutes. After the first sync, updates run incrementally and changes are usually live within minutes.',
  },
  {
    id: 'edit-product-details',
    category: 'products-inventory',
    question: 'Can I edit product details inside Tixxly?',
    answer:
      "Pricing, availability, and core inventory data flow from your booking system — edit those there.\n\nInside Tixxly you can override AI-facing copy (description, highlights, keywords) and toggle activation. Overrides are versioned, so you can revert at any time.",
  },
  {
    id: 'why-deactivate-experiences',
    category: 'products-inventory',
    question: 'Why would I deactivate an experience?',
    answer:
      "Common reasons: a seasonal product that isn't running right now, a product undergoing changes, or a low-margin product you don't want AI agents to recommend. Deactivated experiences stop appearing in AI feeds within ~10 minutes.",
  },
  {
    id: 'refresh-inventory',
    category: 'products-inventory',
    question: 'My inventory looks out of date — how do I refresh it?',
    answer:
      "Inventory → Feeds → click the feed → 'Re-sync now'. This pulls fresh data from your booking system.\n\nIf only one experience is wrong, open the experience and click 'Refresh from provider' to refetch just that product.",
  },

  // ─── Payments & Payouts ───────────────────────────────────────────────────
  {
    id: 'why-stripe-required',
    category: 'payments-payouts',
    question: 'Why does Tixxly require Stripe?',
    answer:
      "Stripe Connect lets us route booking payments directly to your bank. Tixxly never holds your money.\n\nYou are the merchant of record on every transaction, which means refund handling, chargebacks, and tax reporting stay under your control — not ours.",
  },
  {
    id: 'stripe-fees',
    category: 'payments-payouts',
    question: 'What are the Stripe fees?',
    answer:
      "Standard Stripe processing fees apply (typically 2.9% + 30¢ for online card payments in the US, varies by country). Tixxly does not add a processing fee on top.\n\nYour total cost is your monthly Tixxly plan plus Stripe's processing fees — no commissions, no per-booking surcharges from us.",
  },
  {
    id: 'when-do-i-get-paid',
    category: 'payments-payouts',
    question: 'When do I get paid?',
    answer:
      "Tixxly releases payouts 48 hours after the travel date — not when the booking is taken. This protects both sides: travelers have a window to raise issues with no-shows or cancellations, and you get paid for experiences that actually happened.\n\nOnce released, Stripe transfers the funds to your bank on its standard schedule for your country (typically 2 business days after release).",
  },
  {
    id: 'when-customer-is-charged',
    category: 'payments-payouts',
    question: "When is the customer's card actually charged?",
    answer:
      "Immediately at booking. The full amount is captured on the customer's card the moment the reservation confirms — your Stripe account shows the successful charge right away.\n\nBeing paid into your bank is a separate step: Tixxly holds payouts until 48 hours after the travel date so refunds and no-shows resolve cleanly. See 'When do I get paid?' for the payout timeline.",
  },
  {
    id: 'no-stripe-account',
    category: 'payments-payouts',
    question: "I don't have a Stripe account — what do I do?",
    answer:
      "Create one at stripe.com — it takes about 10 minutes if you have your business documents ready. After that, return to Payout Setup and click 'Connect Stripe'.\n\nStripe is available in 40+ countries. If Stripe isn't supported where you operate, contact us — Adyen and PayPal are on the roadmap.",
  },
  {
    id: 'why-email-and-country-for-stripe',
    category: 'payments-payouts',
    question: 'Why does Tixxly ask for an email and country before Stripe setup?',
    answer:
      "Stripe needs both to create your Connect account: the email becomes the primary contact on the Stripe side, and the country determines which Stripe Connect product, currencies, and tax forms apply. We confirm them with you up-front so the wrong values don't end up on your Stripe account, where some are hard to change later.\n\nYou can edit either before clicking 'Connect Stripe Account'. The values you confirm here are stored separately from your business contact_info and used only for the payment provider.",
  },
  {
    id: 'country-not-in-list',
    category: 'payments-payouts',
    question: "My country isn't in the Stripe country dropdown — what now?",
    answer:
      "Stripe Connect supports about 40 countries today. If yours isn't in the list, Stripe doesn't yet allow Tixxly to onboard you with a Stripe account.\n\nEmail hello@tixxly.ai with your country and we'll let you know when Stripe adds support — and when our alternate provider integrations (Adyen, PayPal) go live.",
  },
  {
    id: 'disconnect-stripe',
    category: 'payments-payouts',
    question: 'How do I disconnect or switch to a different Stripe account?',
    answer:
      "Payout Setup → Disconnect Stripe Account. This unlinks Stripe from your Tixxly account so you can connect a fresh one — useful when consolidating to a single Stripe account or moving entities.\n\nThe Stripe account itself isn't deleted. It stays accessible from your Stripe dashboard with all its history. You won't be able to take new bookings while disconnected, so reconnect promptly if you have an active storefront.",
  },
  {
    id: 'how-refunds-work',
    category: 'payments-payouts',
    question: 'How do refunds work?',
    answer:
      "Customers see your cancellation policy at checkout (set in Settings → Merchant Details → Return Policy URL).\n\nRefunds are issued through your Stripe dashboard or via the booking detail page in Tixxly. The customer-facing 'Manage booking' link on your storefront lets travelers request refunds within your policy window.",
  },

  // ─── Supplier Agreement ───────────────────────────────────────────────────
  {
    id: 'whats-in-the-agreement',
    category: 'supplier-agreement',
    question: "What's in the supplier agreement?",
    answer:
      "The Supplier Services Agreement (SSA) covers your relationship with Tixxly: how we distribute your inventory, what data we share with AI platforms, the data processing addendum (DPA), and standard liability terms.\n\nYou can read the full agreement on the Agreement page and download a PDF for your records.",
  },
  {
    id: 'why-agreement-required',
    category: 'supplier-agreement',
    question: 'Why do I have to accept it?',
    answer:
      "It's the legal basis for Tixxly distributing your inventory to AI ecosystems. Without it we can't share product data with ChatGPT, Gemini, Claude, or other AI platforms on your behalf.",
  },
  {
    id: 'can-i-negotiate-terms',
    category: 'supplier-agreement',
    question: 'Can I negotiate terms?',
    answer:
      'Free, Growth, and Pro vendors use the standard SSA. Infrastructure-tier customers can negotiate custom terms — including custom DPAs, SLA commitments, and direct AI checkout terms. Contact us for details.',
  },
  {
    id: 'who-is-merchant-of-record',
    category: 'supplier-agreement',
    question: 'Who is the merchant of record on bookings?',
    answer:
      "You are. Tixxly is infrastructure — bookings are commercial transactions between your business and the customer. This is why payments route through your Stripe account and why your business name (not Tixxly's) appears on the customer's card statement.",
  },

  // ─── AI Storefront & Discovery ────────────────────────────────────────────
  {
    id: 'what-is-ai-storefront',
    category: 'ai-storefront',
    question: 'What is the AI storefront?',
    answer:
      "Your AI storefront is a public, AI-readable page at stores.tixxly.ai/v/<your-username>. It lists your activated experiences with structured data (Schema.org markup and an llms.txt feed).\n\nAI agents read this page to surface your products in conversations like 'find a kayak tour in Lisbon next Saturday'. Travelers can then book directly through the storefront.",
  },
  {
    id: 'how-ai-discovery-works',
    category: 'ai-storefront',
    question: 'How does AI discovery actually work?',
    answer:
      'Tixxly publishes your inventory in three ways:\n\n1. Schema.org structured markup on every storefront page — readable by general-purpose web crawlers.\n2. An llms.txt feed designed specifically for LLM crawlers.\n3. A direct merchant feed to OpenAI today; Gemini and Claude integrations are on the roadmap.\n\nAI agents pull from these sources and recommend your products when they match a traveler intent.',
  },
  {
    id: 'custom-domain',
    category: 'ai-storefront',
    question: 'Can I use my own domain instead of stores.tixxly.ai?',
    answer:
      "Yes — and we strongly recommend you do. A branded domain (e.g. travel.yourbrand.com) is the single biggest signal AI agents and travelers use to identify and trust your storefront, so we make custom domains available on every plan, including Free.\n\nIn Settings → Domain, add the CNAME record we show you to your DNS provider. HTTPS is provisioned automatically. You can keep stores.tixxly.ai/v/<your-username> as a fallback while DNS propagates.",
  },
  {
    id: 'rank-higher-in-ai',
    category: 'ai-storefront',
    question: 'How do I rank higher in AI recommendations?',
    answer:
      'Three levers, in order of impact:\n\n1. Trust Score — fulfillment rate, refund rate, and review sentiment (Pro+).\n2. AI-optimized product copy — clear titles, concrete highlights, accurate keywords. The Optimizer in Inventory → Experiences flags weak copy.\n3. Real-time availability — products with live availability rank above static ones because AI agents can confirm bookability before recommending.',
  },
  {
    id: 'product-not-in-chatgpt',
    category: 'ai-storefront',
    question: "I searched ChatGPT and don't see my product — why?",
    answer:
      'AI training and indexing cycles lag publication. New products usually appear in conversational results within 7–14 days.\n\nMake sure: your storefront status is active (Settings), the experience is activated (Inventory → Experiences), and your Schema.org data is valid — we surface validation errors directly on each experience page.',
  },

  // ─── Pricing & Plans ──────────────────────────────────────────────────────
  {
    id: 'plan-differences',
    category: 'pricing-plans',
    question: "What's the difference between Free, Growth, and Pro?",
    answer:
      "Free ($0/mo): get AI-ready, with an AI booking storefront.\nGrowth ($99/mo): unlimited bookings plus AI discovery optimization.\nPro ($199/mo): adds Trust Score, AI ranking optimization, conversion analytics, and API access.\nInfrastructure (custom): direct AI checkout (Instant Checkout), real-time availability endpoints, dedicated onboarding, and a named account manager.\n\nCustom domains are available on every plan — they help AI agents and travelers identify your brand, so we don't gate them behind a tier.\n\nYou can change plans at any time from Subscriptions → Manage Plan.",
  },
  {
    id: 'change-plan',
    category: 'pricing-plans',
    question: 'How do I change plans?',
    answer:
      'Subscriptions → Manage Plan. Upgrades take effect immediately and are prorated for the rest of the current period. Downgrades take effect at the end of your current billing period so you keep access to paid features until then.',
  },
  {
    id: 'do-you-take-commission',
    category: 'pricing-plans',
    question: 'Do you take a commission on bookings?',
    answer:
      'No. Tixxly is subscription SaaS only. You pay a flat monthly fee and keep 100% of booking revenue (after Stripe processing fees, which go to Stripe — not us).',
  },
  {
    id: 'annual-billing',
    category: 'pricing-plans',
    question: 'Is there a discount for annual billing?',
    answer:
      'Yes — annual plans get roughly two months free compared with monthly. Toggle to annual on the pricing page or in Subscriptions → Manage Plan.',
  },

  // ─── Account & Security ───────────────────────────────────────────────────
  {
    id: 'add-team-member',
    category: 'account-security',
    question: 'How do I add a team member to my account?',
    answer:
      "Settings → Team → Invite User. Enter their email and choose a role:\n\n- Admin: can manage everything including billing, integrations, and settings.\n- Viewer: can see analytics and bookings but can't change settings.\n\nThey'll receive an email invite to sign in.",
  },
  {
    id: 'forgot-password',
    category: 'account-security',
    question: 'I forgot my password — how do I reset it?',
    answer:
      "On the sign-in page click 'Forgot password?'. We'll email a reset link to your account email. If it doesn't arrive in a few minutes, check spam or contact support.",
  },
  {
    id: 'data-export',
    category: 'account-security',
    question: 'Can I export my data?',
    answer:
      'Yes. Bookings can be exported as CSV from the Bookings page. Product data is owned by your booking system — export it from there.\n\nFor a full account archive (Pro+), email support@tixxly.ai and we will get back to you with the export.',
  },
]
